/**
 * A switch that says what it does — checkbox + label, hit target included.
 *
 * Moved out of `CustomFieldsSettingsCard` when the service types card needed the same "In
 * use" switch, so those two share one. `AutomationsCard` still draws its own switches.
 *
 * `ariaLabel` names the switch when a list shows the same `label` on every row ("In use" six
 * times reads as six identical controls to a screen reader). Keep the visible label inside
 * it, so a spoken command matches what is on screen.
 *
 * `focusableWhenDisabled` keeps a disabled switch focused and in the tab order: it says
 * `aria-disabled` instead of `disabled`, and a change is ignored. A `disabled` control drops
 * focus to <body> the moment it disables, which on a card that locks while its own write
 * saves means every flip sends a keyboard user back to the top of the page.
 */
export function Toggle({
  label,
  ariaLabel,
  on,
  disabled,
  focusableWhenDisabled = false,
  onChange,
}: {
  label: string;
  ariaLabel?: string;
  on: boolean;
  disabled: boolean;
  focusableWhenDisabled?: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label className="flex min-h-11 shrink-0 cursor-pointer items-center gap-1.5 text-xs">
      <input
        type="checkbox"
        className="size-4 aria-disabled:opacity-50"
        checked={on}
        disabled={focusableWhenDisabled ? undefined : disabled}
        aria-disabled={focusableWhenDisabled ? disabled : undefined}
        aria-label={ariaLabel}
        // Controlled, so an ignored change leaves the box as `on` says.
        onChange={(e) => {
          if (!disabled) onChange(e.target.checked);
        }}
      />
      <span className={on ? '' : 'text-muted-foreground'}>{label}</span>
    </label>
  );
}
